import { z } from 'zod';
import { identifierSchema } from './common.js';
/**
 * Authentication shapes.
 *
 * Two ways in — Endeavor X federation and an admin-issued local password — and one session model
 * behind both. Every response schema here is also the serialization filter, which is why none of
 * them has a field for a password hash, a session id or an invite token: a column added to
 * `credentials` later cannot leak through an endpoint that was never meant to expose it.
 */
export const userRoleSchema = z.enum(['admin', 'member']);
/**
 * How this account signs in. Derived, not stored: a row in `identities` means federation, a row
 * in `credentials` means a local password, and an admin-invited user who has also been federated
 * has both.
 */
export const authMethodSchema = z.enum(['endeavor_x', 'password']);
/**
 * The one message a failed sign-in is allowed to produce.
 *
 * Defined once, here, because the requirement is that the unknown-account and wrong-password
 * responses are *byte-identical* (00-context §10.5). Two string literals in two handlers satisfy
 * that until somebody edits one of them.
 */
export const GENERIC_LOGIN_ERROR = 'Invalid email or password';
/**
 * Twelve, per prompt 09. Length is the only password rule that survives contact with users;
 * composition rules push them towards `Passw0rd!` and are not enforced here.
 */
export const PASSWORD_MIN_LENGTH = 12;
/** Argon2id's own limit is far higher, but hashing is deliberately slow — so cap the input. */
export const PASSWORD_MAX_LENGTH = 256;
/**
 * `z.email()` rather than a hand-rolled pattern, and lowercased on the way in.
 *
 * The database column is `citext`, so storage and lookup are already case-insensitive; the
 * transform is about what we *log* and what an invite URL is minted for, which should not depend
 * on how the user capitalised their own address.
 */
export const emailSchema = z
    .email()
    .max(254)
    .transform((value) => value.trim().toLowerCase())
    .meta({ example: 'someone@pearson.com' });
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `must be at least ${String(PASSWORD_MIN_LENGTH)} characters`)
    .max(PASSWORD_MAX_LENGTH)
    .meta({ example: 'correct-horse-battery-staple' });
/**
 * Deliberately *not* `passwordSchema`.
 *
 * A login body must accept any string, because rejecting a short password at the schema is a 400
 * that says "that is not even long enough to be one of ours" — which is information a login
 * endpoint does not owe an anonymous caller. Wrong-length and wrong-password produce the same
 * 401 and the same sentence.
 */
export const loginPasswordSchema = z.string().min(1).max(PASSWORD_MAX_LENGTH);
export const displayNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[^\p{Cc}]+$/u, 'must not contain control characters')
    .meta({ example: 'Alex Fernandes' });
/** The current user. What `/auth/me` returns and what the web app's session context holds. */
export const authUserSchema = z
    .object({
    id: z.string(),
    email: z.string(),
    displayName: z.string().nullable(),
    role: userRoleSchema,
    authMethods: z.array(authMethodSchema),
})
    .describe('The authenticated user');
export const meResponseSchema = z.object({ user: authUserSchema });
export const loginRequestSchema = z
    .object({ email: emailSchema, password: loginPasswordSchema })
    .describe('Local email and password. Federated users sign in at /auth/ex/login instead.');
export const changePasswordRequestSchema = z.object({
    currentPassword: loginPasswordSchema,
    newPassword: passwordSchema,
});
/** `{ ok: true }`, for the state changes whose only interesting outcome is the status code. */
export const okResponseSchema = z.object({ ok: z.literal(true) });
export const createInviteRequestSchema = z.object({
    email: emailSchema,
    role: userRoleSchema.default('member'),
});
/** What an admin sees in the user-management table (prompt 12). Never includes the token hash. */
export const inviteSummarySchema = z.object({
    id: z.string(),
    email: z.string(),
    role: userRoleSchema,
    expiresAt: z.string().describe('ISO 8601'),
    createdAt: z.string().describe('ISO 8601'),
    acceptedAt: z.string().nullable().describe('ISO 8601; null while the invite is outstanding'),
    invitedByEmail: z.string().nullable(),
    /** Derived, so the UI does not re-implement the expiry comparison. */
    status: z.enum(['pending', 'accepted', 'expired']),
});
export const inviteListResponseSchema = z.object({ invites: z.array(inviteSummarySchema) });
/**
 * The creation response, and the only time the raw token exists outside the admin's browser.
 *
 * It is not persisted — only its SHA-256 hash is — so an admin who loses the URL revokes the
 * invite and issues another one. There is no "show me that token again" endpoint, by design.
 */
export const inviteCreatedResponseSchema = z.object({
    invite: inviteSummarySchema,
    token: z.string().describe('Shown exactly once. Never stored and never logged.'),
    inviteUrl: z.string().describe('The one-time acceptance URL to send to the invitee.'),
});
/**
 * What the acceptance form needs, and nothing more.
 *
 * Just the email, so the invitee can see which address they are claiming. No role, no inviter,
 * and no hint that a token which does not validate ever existed.
 */
export const invitePreviewResponseSchema = z.object({
    email: z.string(),
    expiresAt: z.string().describe('ISO 8601'),
});
export const acceptInviteRequestSchema = z.object({
    password: passwordSchema,
    displayName: displayNameSchema,
});
/**
 * A 32-byte random token rendered base64url — 43 characters, no padding.
 *
 * Constrained at the edge rather than looked up loosely: an input that cannot possibly be one of
 * our tokens is rejected before it reaches a database round trip, and the length check is part of
 * what keeps that lookup from being a timing oracle.
 */
export const inviteTokenParamSchema = z.object({
    token: z
        .string()
        .length(43)
        .regex(/^[A-Za-z0-9_-]+$/u, 'is not a valid invite token')
        .meta({ example: 'hOT6bLsZ3k9wQ1rXvK4pY7mN2sD8fG0jH5tA1cE6uI3' }),
});
export const inviteIdParamSchema = z.object({ id: identifierSchema });
/** The SSO login redirect. `returnTo` is validated against an allowlist of internal paths. */
export const exLoginQuerySchema = z.object({
    returnTo: z
        .string()
        .max(512)
        .optional()
        .describe('An internal path to return to after sign-in, such as `/courses/9780137928576`. ' +
        'Absolute URLs and protocol-relative paths are rejected with a 400.')
        .meta({ example: '/search' }),
});
/**
 * The OIDC callback, as the provider sends it.
 *
 * `code`/`state` on success and `error`/`error_description` on failure — both optional, because
 * exactly one pair arrives and the handler decides which. Validated only for shape here; every
 * cryptographic check happens in `openid-client`.
 */
export const exCallbackQuerySchema = z.object({
    code: z.string().min(1).max(4096).optional(),
    state: z.string().min(1).max(512).optional(),
    error: z.string().max(256).optional(),
    error_description: z.string().max(1024).optional(),
    iss: z.string().max(512).optional(),
});
/** Whether the SSO button should be rendered at all (prompt 11 reads this at `/auth/config`). */
export const authConfigResponseSchema = z.object({
    ssoEnabled: z.boolean(),
    ssoLoginPath: z.string(),
});
//# sourceMappingURL=auth.js.map